/**
 * FilterRangeSlider - Range input for numeric filters
 *
 * Single or dual-handle slider for filtering by numeric ranges.
 * Displays current value(s) with optional unit label.
 */

import { useCallback } from 'react';

import type { FilterRangeSliderProps } from './Filter.types';

export function FilterRangeSlider({
  value,
  min,
  max,
  step = 1,
  unit = '',
  onChange,
  dual = false,
  ariaLabel,
  className = '',
}: FilterRangeSliderProps): React.JSX.Element {
  const handleName = ariaLabel || unit || 'value';
  const currentValue = Array.isArray(value) ? value : [min, value];
  const [rangeMin, rangeMax] = currentValue;

  const handleMinChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newMin = Number(e.target.value);
      if (dual) {
        onChange([Math.min(newMin, rangeMax - step), rangeMax]);
      } else {
        onChange(newMin);
      }
    },
    [dual, onChange, rangeMax, step]
  );

  const handleMaxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newMax = Number(e.target.value);
      if (dual) {
        onChange([rangeMin, Math.max(newMax, rangeMin + step)]);
      } else {
        onChange(newMax);
      }
    },
    [dual, onChange, rangeMin, step]
  );

  const formatValue = (val: number) => {
    return unit ? `${val} ${unit}` : val.toString();
  };

  const progressPercent = dual
    ? {
        left: ((rangeMin - min) / (max - min)) * 100,
        right: ((rangeMax - min) / (max - min)) * 100,
      }
    : {
        left: 0,
        right: ((rangeMax - min) / (max - min)) * 100,
      };

  return (
    <div
      className={`filter-range-slider px-3 py-2 ${className}`.trim()}
      data-testid="filter-range-slider"
    >
      {/* Value display */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-body-md font-mono tabular-nums text-neutral-100">
          {dual ? formatValue(rangeMin) : formatValue(min)}
        </span>
        <span className="text-body-md font-mono tabular-nums text-neutral-100">
          {formatValue(rangeMax)}
        </span>
      </div>

      {/* Slider track */}
      <div className="relative h-6 flex items-center">
        {/* Background track */}
        <div className="absolute inset-x-0 h-1.5 bg-contrast-600 rounded-full" />

        {/* Active track */}
        <div
          className="absolute h-1.5 bg-primary-400/40 rounded-full"
          style={{
            left: `${progressPercent.left}%`,
            right: `${100 - progressPercent.right}%`,
          }}
        />

        {/* Min handle (dual mode only) */}
        {dual && (
          <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={rangeMin}
            onChange={handleMinChange}
            className="
              absolute inset-x-0
              w-full h-6
              appearance-none bg-transparent
              pointer-events-none
              [&::-webkit-slider-thumb]:appearance-none
              [&::-webkit-slider-thumb]:w-4
              [&::-webkit-slider-thumb]:h-4
              [&::-webkit-slider-thumb]:rounded-full
              [&::-webkit-slider-thumb]:bg-white-50
              [&::-webkit-slider-thumb]:border
              [&::-webkit-slider-thumb]:border-stroke-default
              [&::-webkit-slider-thumb]:cursor-pointer
              [&::-webkit-slider-thumb]:pointer-events-auto
              [&::-webkit-slider-thumb]:transition-all
              [&::-webkit-slider-thumb]:duration-150
              [&::-webkit-slider-thumb]:hover:border-stroke-strong
              [&::-webkit-slider-thumb]:hover:bg-neutral-50
              [&::-webkit-slider-thumb]:hover:scale-110
              [&::-moz-range-thumb]:w-4
              [&::-moz-range-thumb]:h-4
              [&::-moz-range-thumb]:rounded-full
              [&::-moz-range-thumb]:bg-white-50
              [&::-moz-range-thumb]:border
              [&::-moz-range-thumb]:border-stroke-default
              [&::-moz-range-thumb]:cursor-pointer
              [&::-moz-range-thumb]:pointer-events-auto
              focus:outline-none
              focus-visible:[&::-webkit-slider-thumb]:ring-2
              focus-visible:[&::-webkit-slider-thumb]:ring-primary-400/50
            "
            style={{ zIndex: 3 }}
            aria-label={`Minimum ${handleName}`}
          />
        )}

        {/* Max handle (or single handle) */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={rangeMax}
          onChange={handleMaxChange}
          className="
            absolute inset-x-0
            w-full h-6
            appearance-none bg-transparent
            pointer-events-none
            [&::-webkit-slider-thumb]:appearance-none
            [&::-webkit-slider-thumb]:w-4
            [&::-webkit-slider-thumb]:h-4
            [&::-webkit-slider-thumb]:rounded-full
            [&::-webkit-slider-thumb]:bg-white-50
            [&::-webkit-slider-thumb]:border
            [&::-webkit-slider-thumb]:border-stroke-default
            [&::-webkit-slider-thumb]:cursor-pointer
            [&::-webkit-slider-thumb]:pointer-events-auto
            [&::-webkit-slider-thumb]:transition-all
            [&::-webkit-slider-thumb]:duration-150
            [&::-webkit-slider-thumb]:hover:border-stroke-strong
            [&::-webkit-slider-thumb]:hover:bg-neutral-50
            [&::-webkit-slider-thumb]:hover:scale-110
            [&::-moz-range-thumb]:w-4
            [&::-moz-range-thumb]:h-4
            [&::-moz-range-thumb]:rounded-full
            [&::-moz-range-thumb]:bg-white-50
            [&::-moz-range-thumb]:border
            [&::-moz-range-thumb]:border-stroke-default
            [&::-moz-range-thumb]:cursor-pointer
            [&::-moz-range-thumb]:pointer-events-auto
            focus:outline-none
            focus-visible:[&::-webkit-slider-thumb]:ring-2
            focus-visible:[&::-webkit-slider-thumb]:ring-primary-400/50
          "
          style={{ zIndex: 2 }}
          aria-label={
            dual ? `Maximum ${handleName}` : ariaLabel || unit || 'Value'
          }
        />
      </div>

      {/* Min/Max labels */}
      <div className="flex items-center justify-between mt-1">
        <span className="text-label-xs text-neutral-500">{min}</span>
        <span className="text-label-xs text-neutral-500">{max}</span>
      </div>
    </div>
  );
}

export default FilterRangeSlider;
